import React, { useState, useEffect } from 'react';
import { Calendar, Phone, Mail, MapPin, Clock, User, Home as HomeIcon, MessageSquare, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, contacted

  const fetchBookings = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bookings`);
      setBookings(response.data);
      toast.success('Bookings loaded successfully!');
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeSlot = (time) => {
    const slots = {
      'morning': 'Morning (8:00 AM - 12:00 PM)',
      'afternoon': 'Afternoon (12:00 PM - 4:00 PM)',
      'evening': 'Evening (4:00 PM - 6:00 PM)'
    };
    return slots[time] || time;
  };

  const filteredBookings = bookings.filter(booking => {
    if (filter === 'all') return true;
    return booking.status === filter;
  });

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-brand-navy">Booking Requests</h1>
              <p className="text-gray-600 mt-2">Manage all your customer booking requests</p>
            </div>
            <Button 
              onClick={fetchBookings} 
              className="bg-brand-navy hover:bg-brand-navy-dark text-white"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="border-2 border-brand-blue">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Bookings</p>
                    <p className="text-3xl font-bold text-brand-navy">{bookings.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-brand-blue/20 rounded-full flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-brand-blue" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-orange-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Pending</p>
                    <p className="text-3xl font-bold text-orange-600">
                      {bookings.filter(b => b.status === 'pending').length}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <Clock className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">This Week</p>
                    <p className="text-3xl font-bold text-green-600">
                      {bookings.filter(b => {
                        const bookingDate = new Date(b.createdAt);
                        const weekAgo = new Date();
                        weekAgo.setDate(weekAgo.getDate() - 7);
                        return bookingDate > weekAgo;
                      }).length}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <User className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filter Buttons */}
          <div className="flex gap-2">
            <Button 
              onClick={() => setFilter('all')}
              variant={filter === 'all' ? 'default' : 'outline'}
              className={filter === 'all' ? 'bg-brand-navy text-white' : ''}
            >
              All ({bookings.length})
            </Button>
            <Button 
              onClick={() => setFilter('pending')}
              variant={filter === 'pending' ? 'default' : 'outline'}
              className={filter === 'pending' ? 'bg-orange-600 text-white' : ''}
            >
              Pending ({bookings.filter(b => b.status === 'pending').length})
            </Button>
          </div>
        </div>

        {/* Bookings List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-12 w-12 animate-spin text-brand-navy mx-auto mb-4" />
            <p className="text-gray-600">Loading bookings...</p>
          </div>
        ) : filteredBookings.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No bookings yet</h3>
              <p className="text-gray-600">When customers book appointments, they'll appear here.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map((booking) => (
              <Card key={booking.id} className="border-2 hover:border-brand-gold transition-all duration-200">
                <CardHeader className="bg-gradient-to-r from-brand-navy/5 to-brand-blue/5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <CardTitle className="text-xl text-brand-navy">{booking.name}</CardTitle>
                        <Badge className="bg-orange-500 text-white">
                          {booking.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600">
                        Booking ID: {booking.id}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Submitted: {formatDate(booking.createdAt)}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-brand-blue text-white text-sm px-3 py-1">
                        {booking.serviceType}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Contact Info */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-brand-navy mb-3">Contact Information</h4>
                      <div className="flex items-center space-x-3">
                        <Phone className="h-4 w-4 text-brand-blue flex-shrink-0" />
                        <a href={`tel:${booking.phone}`} className="text-brand-navy hover:text-brand-gold font-medium">
                          {booking.phone}
                        </a>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Mail className="h-4 w-4 text-brand-blue flex-shrink-0" />
                        <a href={`mailto:${booking.email}`} className="text-brand-navy hover:text-brand-gold">
                          {booking.email}
                        </a>
                      </div>
                      <div className="flex items-start space-x-3">
                        <MapPin className="h-4 w-4 text-brand-blue flex-shrink-0 mt-1" />
                        <span className="text-gray-700">{booking.address}</span>
                      </div>
                    </div>

                    {/* Service Details */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-brand-navy mb-3">Service Details</h4>
                      <div className="flex items-center space-x-3">
                        <HomeIcon className="h-4 w-4 text-brand-blue flex-shrink-0" />
                        <span className="text-gray-700">
                          <span className="font-medium">Property:</span> {booking.propertyType}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Calendar className="h-4 w-4 text-brand-blue flex-shrink-0" />
                        <span className="text-gray-700">
                          <span className="font-medium">Date:</span> {booking.preferredDate}
                        </span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Clock className="h-4 w-4 text-brand-blue flex-shrink-0 mt-1" />
                        <span className="text-gray-700">
                          <span className="font-medium">Time:</span> {getTimeSlot(booking.preferredTime)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  {booking.message && (
                    <div className="mt-6 pt-6 border-t">
                      <div className="flex items-start space-x-3">
                        <MessageSquare className="h-4 w-4 text-brand-blue flex-shrink-0 mt-1" />
                        <div className="flex-1">
                          <p className="font-medium text-brand-navy mb-2">Additional Notes:</p>
                          <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{booking.message}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="mt-6 pt-6 border-t flex gap-3">
                    <a href={`tel:${booking.phone}`}>
                      <Button className="bg-brand-gold hover:bg-brand-gold-dark text-brand-navy font-semibold">
                        <Phone className="h-4 w-4 mr-2" />
                        Call Customer
                      </Button>
                    </a>
                    <a href={`mailto:${booking.email}`}>
                      <Button variant="outline" className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white">
                        <Mail className="h-4 w-4 mr-2" />
                        Send Email
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBookings;
